const EXHIBIT_STYLE_VALUES = new Set(['auto', 'plaintiff', 'defendant', 'neutral']);
const EXHIBIT_PAGE_LABEL_POSITIONS = new Set([
    'top-left', 'top-center', 'top-right',
    'bottom-left', 'bottom-center', 'bottom-right'
]);
const EXHIBIT_PAGE_LABEL_SEPARATORS = new Set(['-', '.']);
const CONVERTIBLE_EXTENSIONS = new Set([
    'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'odt', 'ods', 'odp', 'rtf',
    'jpg', 'jpeg', 'png', 'gif', 'bmp', 'tif', 'tiff', 'webp', 'txt', 'eml', 'msg'
]);

export const buildBatesClientOperationKey = async (
    cryptoApi = typeof window !== 'undefined' ? window.crypto : null
) => {
    if (!cryptoApi?.getRandomValues) {
        throw new Error('Secure Bates task reservation is unavailable in this browser.');
    }
    const bytes = new Uint8Array(32);
    cryptoApi.getRandomValues(bytes);
    const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
    return `batesop_${hex}`;
};

const emptyResult = (reason) => ({
    valid: false,
    value: '',
    styleHint: 'neutral',
    reason
});

const cleanFilenameBase = (filename) => String(filename || '')
    .split(/[\\/]/)
    .pop()
    .replace(/\.[^.]+$/, '')
    .trim();

const normalizeKnownIdentifier = (prefix, number) => `${String(prefix || '').toUpperCase()}${String(number || '').toUpperCase()}`;

const isPlausibleCompactDate = (value) => {
    const digits = String(value || '');
    if (!/^\d{6}$|^\d{8}$/.test(digits)) return false;

    const yearLength = digits.length === 8 ? 4 : 2;
    const month = Number(digits.slice(yearLength, yearLength + 2));
    const day = Number(digits.slice(yearLength + 2, yearLength + 4));
    return month >= 1 && month <= 12 && day >= 1 && day <= 31;
};

export const parseExhibitStampIdentifier = (filename) => {
    const base = cleanFilenameBase(filename);
    if (!base) return emptyResult('The file name is empty.');

    const explicitMatches = [
        ...base.matchAll(/(?:^|[^a-z0-9])(PTX|DTX|JTX)[\s._-]*(\d{1,8}[a-z]?)(?=$|[^a-z0-9])/gi),
        ...base.matchAll(/(?:^|[^a-z0-9])(?:EXHIBIT|EXH)\s*(?:(?:NO|NUMBER)\.?|#)?\s*[-_:]*\s*([a-z]?\d{1,8}[a-z]?)(?=$|[^a-z0-9])/gi)
    ];

    if (explicitMatches.length > 1) {
        return emptyResult('The file name contains more than one possible exhibit identifier.');
    }

    if (explicitMatches.length === 1) {
        const explicitMatch = explicitMatches[0];
        const matchedText = explicitMatch[0];
        const remainder = base.slice((explicitMatch.index || 0) + matchedText.length);
        if (/^\s*[-–—]\s*\d/.test(remainder)) {
            return emptyResult('Exhibit number ranges cannot be stamped automatically.');
        }

        const knownPrefix = explicitMatch[1] && /^(PTX|DTX|JTX)$/i.test(explicitMatch[1])
            ? explicitMatch[1].toUpperCase()
            : null;

        if (!knownPrefix) {
            return {
                valid: true,
                value: explicitMatch[1].toUpperCase(),
                styleHint: 'neutral',
                reason: ''
            };
        }

        const prefix = knownPrefix;
        return {
            valid: true,
            value: normalizeKnownIdentifier(prefix, explicitMatch[2]),
            styleHint: prefix === 'PTX' ? 'plaintiff' : (prefix === 'DTX' ? 'defendant' : 'neutral'),
            reason: ''
        };
    }

    if (/^\s*(?:\d{2}|\d{4})[-_.]\d{1,2}[-_.]\d{1,2}(?:\b|\s)/.test(base)) {
        return emptyResult('A date at the beginning of the file name is not treated as an exhibit number.');
    }

    const leadingIdentifier = base.match(/^\s*#?(\d{1,8}[a-z]?)(?=$|[\s._-])/i);
    if (leadingIdentifier) {
        const numericPart = leadingIdentifier[1].match(/^\d+/)?.[0] || '';
        const remainder = base.slice(leadingIdentifier[0].length);
        if (/\d/.test(remainder)) {
            return emptyResult('The file name contains additional numbers, so the exhibit number is ambiguous.');
        }
        if (/^(?:19|20)\d{2}$/.test(numericPart) || isPlausibleCompactDate(numericPart)) {
            return emptyResult('A date or year is not treated as an exhibit number.');
        }
        return {
            valid: true,
            value: leadingIdentifier[1].toUpperCase(),
            styleHint: 'neutral',
            reason: ''
        };
    }

    return emptyResult('Add PTX, DTX, JTX, Exhibit, or an exhibit number to the beginning of the file name.');
};

export const normalizeExhibitStampStyle = (style) => {
    const normalized = String(style || '').trim().toLowerCase();
    return EXHIBIT_STYLE_VALUES.has(normalized) ? normalized : 'auto';
};

export const canonicalizeExhibitStampIdentifier = (value) => {
    const normalized = String(value || '').trim().toUpperCase().replace(/^#\s*/, '').replace(/[\s._-]+/g, '');
    const match = normalized.match(/^(PTX|DTX|JTX)?([A-Z]?)(\d{1,8})([A-Z]?)$/);
    if (!match) return normalized;

    const numericValue = String(Number(match[3]));
    return `${match[1] || ''}${match[2] || ''}${numericValue}${match[4] || ''}`;
};

export const resolveExhibitStampStyle = (requestedStyle, parsedIdentifier) => {
    const normalized = normalizeExhibitStampStyle(requestedStyle);
    if (normalized !== 'auto') return normalized;
    return parsedIdentifier?.valid && ['plaintiff', 'defendant', 'neutral'].includes(parsedIdentifier.styleHint)
        ? parsedIdentifier.styleHint
        : 'neutral';
};

export const exhibitStampStyleLabel = (style) => {
    switch (normalizeExhibitStampStyle(style)) {
        case 'plaintiff':
            return "PLAINTIFF'S";
        case 'defendant':
            return "DEFENDANT'S";
        default:
            return 'EXHIBIT';
    }
};

export const getBatesFileKey = (file) => String(
    file?.source_key || file?.key || file?.file_path || file?.id || ''
);

const getBatesFolderParentKey = (folderKey) => {
    const folderWithoutTrailingSlash = folderKey.slice(0, -1);
    const finalSlashIndex = folderWithoutTrailingSlash.lastIndexOf('/');
    return finalSlashIndex >= 0 ? folderWithoutTrailingSlash.slice(0, finalSlashIndex + 1) : '';
};

export const getBatesCaseRelativeFolderPath = (folderKey) => {
    const key = String(folderKey || '');
    const match = key.match(/^cases\/[^/\0\r\n]+\/\{\{Name\}\}\/(.*)$/);
    if (!match || (match[1] !== '' && !match[1].endsWith('/'))) return null;
    return match[1];
};

export const areEquivalentBatesFolderOrders = (requestedFolderKeys = [], canonicalFolderKeys = []) => {
    if (!Array.isArray(requestedFolderKeys)
        || !Array.isArray(canonicalFolderKeys)
        || requestedFolderKeys.length === 0
        || requestedFolderKeys.length !== canonicalFolderKeys.length) {
        return false;
    }

    return requestedFolderKeys.every((requestedKey, index) => {
        const requestedPath = getBatesCaseRelativeFolderPath(requestedKey);
        const canonicalPath = getBatesCaseRelativeFolderPath(canonicalFolderKeys[index]);
        return requestedPath !== null && requestedPath === canonicalPath;
    });
};

export const buildBatesFolderOrder = (folderKeys = [], fileKeys = []) => {
    const folders = Array.isArray(folderKeys) ? folderKeys : [];
    const files = Array.isArray(fileKeys) ? fileKeys : [];
    const invalidFolder = folders.some(folderKey => (
        typeof folderKey !== 'string'
        || folderKey.length === 0
        || !folderKey.endsWith('/')
        || /[\0\r\n]/.test(folderKey)
    ));

    if (files.length > 0) {
        return { eligible: false, orderedFolderKeys: [], parentKey: '', reason: 'mixed-selection' };
    }
    if (folders.length === 0) {
        return { eligible: false, orderedFolderKeys: [], parentKey: '', reason: 'no-folders' };
    }
    if (invalidFolder) {
        return { eligible: false, orderedFolderKeys: [], parentKey: '', reason: 'invalid-folder-key' };
    }
    if (new Set(folders).size !== folders.length) {
        return { eligible: false, orderedFolderKeys: [], parentKey: '', reason: 'duplicate-folder' };
    }

    const parentKey = getBatesFolderParentKey(folders[0]);
    if (folders.some(folderKey => getBatesFolderParentKey(folderKey) !== parentKey)) {
        return { eligible: false, orderedFolderKeys: [], parentKey: '', reason: 'different-parents' };
    }

    return {
        eligible: true,
        orderedFolderKeys: [...folders],
        parentKey,
        reason: ''
    };
};

export const validateBatesOrderedAnalyzeResult = (requestedFolderKeys = [], result = {}) => {
    const requestedCount = Array.isArray(requestedFolderKeys) ? requestedFolderKeys.length : 0;
    const canonicalFolderOrder = buildBatesFolderOrder(result?.ordered_folder_keys, []);
    const snapshotFolderKeys = result?.selection_snapshot?.ordered_folder_keys;
    const snapshotMatchesCanonicalOrder = Array.isArray(snapshotFolderKeys)
        && snapshotFolderKeys.length === canonicalFolderOrder.orderedFolderKeys.length
        && snapshotFolderKeys.every((folderKey, index) => (
            folderKey === canonicalFolderOrder.orderedFolderKeys[index]
        ));

    const valid = requestedCount > 0
        && canonicalFolderOrder.eligible
        && canonicalFolderOrder.orderedFolderKeys.length === requestedCount
        && result?.ordered_folder_parent === canonicalFolderOrder.parentKey
        && snapshotMatchesCanonicalOrder;

    return {
        valid,
        canonicalFolderOrder: valid ? canonicalFolderOrder : null
    };
};

export const moveBatesOrderItem = (items, fromIndex, toIndex) => {
    const source = Array.isArray(items) ? items : [];
    if (!Number.isInteger(fromIndex)
        || !Number.isInteger(toIndex)
        || fromIndex < 0
        || toIndex < 0
        || fromIndex >= source.length
        || toIndex >= source.length
        || fromIndex === toIndex) {
        return source;
    }

    const next = [...source];
    const [item] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, item);
    return next;
};

const getFileExtension = (file) => {
    const supplied = String(file?.extension || file?.ext || '').trim().toLowerCase().replace(/^\./, '');
    if (supplied) return supplied;
    const name = String(file?.name || file?.file_name || getBatesFileKey(file)).split(/[?#]/)[0];
    return name.includes('.') ? name.split('.').pop().toLowerCase() : '';
};

const normalizeAnalyzedIdentifier = (parsed, filename) => {
    const source = parsed && typeof parsed === 'object' ? parsed : null;
    if (!source) return parseExhibitStampIdentifier(filename);

    return {
        valid: source.success === true || source.valid === true,
        value: String(source.value || '').trim().toUpperCase(),
        styleHint: String(source.style_hint || source.styleHint || 'neutral').trim().toLowerCase(),
        reason: String(source.message || source.reason || '').trim(),
        errorCode: String(source.error_code || source.errorCode || '').trim()
    };
};

export const normalizeBatesAnalyzeFiles = (files, fallbackFiles = []) => {
    const fallbackByKey = new Map((fallbackFiles || []).map(file => [getBatesFileKey(file), file]));
    const source = Array.isArray(files) ? files : fallbackFiles;
    const seen = new Set();

    return (source || []).reduce((result, rawFile) => {
        const sourceKey = getBatesFileKey(rawFile);
        if (!sourceKey || seen.has(sourceKey)) return result;
        seen.add(sourceKey);

        const fallback = fallbackByKey.get(sourceKey) || {};
        const name = String(rawFile?.name || rawFile?.file_name || fallback?.name || sourceKey.split('/').pop() || 'Document');
        const extension = getFileExtension({ ...fallback, ...rawFile, name });
        const hasServerConversionFlag = typeof rawFile?.conversion_supported === 'boolean';

        result.push({
            ...fallback,
            ...rawFile,
            id: sourceKey,
            key: sourceKey,
            source_key: sourceKey,
            name,
            extension,
            conversion_supported: hasServerConversionFlag
                ? rawFile.conversion_supported
                : CONVERTIBLE_EXTENSIONS.has(extension),
            parsed: normalizeAnalyzedIdentifier(rawFile?.parsed, name)
        });
        return result;
    }, []);
};

export const isValidExhibitIdentifier = (identifier) => {
    const normalized = String(identifier || '').trim().toUpperCase().replace(/^#\s*/, '').replace(/[\s._-]+/g, '');
    return /^(PTX|DTX|JTX)?[A-Z]?\d{1,8}[A-Z]?$/.test(normalized);
};

const normalizeManualExhibitIdentifier = (identifier) => String(identifier || '')
    .trim()
    .toUpperCase()
    .replace(/^#\s*/, '')
    .replace(/[\s._-]+/g, '');

export const formatExhibitDisplayIdentifier = (identifier, mode = 'identifier') => {
    const normalized = normalizeManualExhibitIdentifier(identifier);
    if (mode !== 'number') return normalized;

    const match = normalized.match(/^(PTX|DTX|JTX)?([A-Z]?)(\d{1,8})([A-Z]?)$/);
    if (!match) return '';
    const digits = match[3].replace(/^0+(?=\d)/, '');
    return `${match[2] || ''}${digits}${match[4] || ''}`;
};

export const inferExhibitStampStyle = (identifier) => {
    const normalized = normalizeManualExhibitIdentifier(identifier);
    if (normalized.startsWith('PTX')) return 'plaintiff';
    if (normalized.startsWith('DTX')) return 'defendant';
    return 'neutral';
};

export const buildExhibitFileResolution = (
    files,
    overrides = {},
    defaultStyle = 'auto',
    displayMode = 'identifier'
) => {
    const entries = (files || []).map(file => {
        const sourceKey = getBatesFileKey(file);
        const override = overrides[sourceKey] || {};
        const parsed = {
            valid: file?.parsed?.valid === true || file?.parsed?.success === true,
            value: String(file?.parsed?.value || '').trim(),
            styleHint: file?.parsed?.styleHint || file?.parsed?.style_hint || 'neutral',
            reason: file?.parsed?.reason || file?.parsed?.message || 'The exhibit identifier could not be determined.'
        };
        const hasIdentifierOverride = Object.prototype.hasOwnProperty.call(override, 'identifier');
        const identifier = String(hasIdentifierOverride ? override.identifier : parsed.value).trim();
        const identifierValid = isValidExhibitIdentifier(identifier);
        const normalizedIdentifier = identifierValid ? normalizeManualExhibitIdentifier(identifier) : '';
        const canonicalIdentifier = identifierValid ? canonicalizeExhibitStampIdentifier(normalizedIdentifier) : '';
        const hasDisplayIdentifierOverride = Object.prototype.hasOwnProperty.call(override, 'display_identifier');
        const displayIdentifier = String(hasDisplayIdentifierOverride
            ? override.display_identifier
            : formatExhibitDisplayIdentifier(normalizedIdentifier, displayMode)).trim();
        const displayIdentifierValid = identifierValid && isValidExhibitIdentifier(displayIdentifier);
        const normalizedDisplayIdentifier = displayIdentifierValid
            ? normalizeManualExhibitIdentifier(displayIdentifier)
            : '';
        const canonicalDisplayIdentifier = displayIdentifierValid
            ? canonicalizeExhibitStampIdentifier(normalizedDisplayIdentifier)
            : '';
        const requestedStyle = normalizeExhibitStampStyle(override.stamp_style || defaultStyle);
        const styleHint = inferExhibitStampStyle(identifier);
        const stampStyle = resolveExhibitStampStyle(requestedStyle, {
            valid: identifierValid,
            styleHint
        });

        return {
            file,
            sourceKey,
            processable: file?.conversion_supported !== false
                && file?.available !== false
                && file?.source_available !== false
                && file?.parsed?.errorCode !== 'source_file_not_found',
            autoResolved: parsed.valid,
            identifier,
            normalizedIdentifier,
            identifierValid,
            canonicalIdentifier,
            displayIdentifier,
            normalizedDisplayIdentifier,
            displayIdentifierValid,
            canonicalDisplayIdentifier,
            requestedStyle,
            stampStyle,
            reason: !identifierValid
                ? (parsed.reason || 'Enter an exhibit identifier containing a number.')
                : (!displayIdentifierValid ? 'Enter a valid visual stamp identifier.' : '')
        };
    });

    const canonicalCounts = entries.reduce((counts, entry) => {
        if (!entry.processable || !entry.identifierValid) return counts;
        counts[entry.canonicalIdentifier] = (counts[entry.canonicalIdentifier] || 0) + 1;
        return counts;
    }, {});
    const displayCanonicalCounts = entries.reduce((counts, entry) => {
        if (!entry.processable || !entry.displayIdentifierValid) return counts;
        counts[entry.canonicalDisplayIdentifier] = (counts[entry.canonicalDisplayIdentifier] || 0) + 1;
        return counts;
    }, {});

    const resolvedEntries = entries.map(entry => {
        const duplicate = entry.processable
            && entry.identifierValid
            && (canonicalCounts[entry.canonicalIdentifier] || 0) > 1;
        const duplicateDisplay = entry.processable
            && entry.displayIdentifierValid
            && (displayCanonicalCounts[entry.canonicalDisplayIdentifier] || 0) > 1;
        return {
            ...entry,
            duplicate,
            duplicateDisplay,
            resolved: !entry.processable
                || (entry.identifierValid && entry.displayIdentifierValid && !duplicate && !duplicateDisplay),
            reason: duplicate
                ? `The exhibit identifier ${entry.identifier} appears more than once in this batch.`
                : (duplicateDisplay
                    ? `The visual stamp identifier ${entry.displayIdentifier} appears more than once in this batch.`
                    : entry.reason)
        };
    });
    const processable = resolvedEntries.filter(entry => entry.processable);
    const unresolved = processable.filter(entry => !entry.resolved);

    return {
        entries: resolvedEntries,
        processable,
        unsupported: resolvedEntries.filter(entry => !entry.processable),
        unresolved,
        canStart: processable.length > 0 && unresolved.length === 0,
        exhibitOverrides: processable
            .filter(entry => entry.resolved && (
                Object.prototype.hasOwnProperty.call(overrides[entry.sourceKey] || {}, 'identifier')
                || entry.normalizedDisplayIdentifier !== entry.normalizedIdentifier
            ))
            .map(entry => ({
                source_key: entry.sourceKey,
                identifier: entry.normalizedIdentifier,
                ...(entry.normalizedDisplayIdentifier !== entry.normalizedIdentifier
                    ? { display_identifier: entry.normalizedDisplayIdentifier }
                    : {}),
                stamp_style: entry.stampStyle
            }))
    };
};

export const formatExhibitSequenceIdentifier = ({ format, customFormat, number, padding }) => {
    const sequence = String(Math.max(0, Number(number) || 0)).padStart(Math.max(1, Number(padding) || 1), '0');
    switch (format) {
        case 'PTX':
        case 'DTX':
        case 'JTX':
            return `${format}${sequence}`;
        case 'number':
            return sequence;
        case 'custom': {
            const template = String(customFormat || '').trim();
            if (!template) return '';
            return template.includes('{n}') ? template.split('{n}').join(sequence) : `${template}${sequence}`;
        }
        default:
            return '';
    }
};

export const applyExhibitSequence = (resolution, overrides, options) => {
    const applyToAll = options?.scope === 'all';
    const targets = applyToAll
        ? (resolution?.processable || [])
        : (resolution?.unresolved || []);
    const targetKeys = new Set(targets.map(entry => entry.sourceKey));
    const reserved = new Set((resolution?.entries || [])
        .filter(entry => entry.processable && !targetKeys.has(entry.sourceKey) && entry.identifierValid)
        .map(entry => entry.canonicalIdentifier));
    const next = { ...(overrides || {}) };
    let number = Math.max(0, Number(options?.startNumber) || 0);

    targets.forEach(entry => {
        let identifier = '';
        let attempts = 0;
        do {
            identifier = formatExhibitSequenceIdentifier({
                format: options?.format,
                customFormat: options?.customFormat,
                number,
                padding: options?.padding
            });
            number += 1;
            attempts += 1;
        } while (identifier && reserved.has(canonicalizeExhibitStampIdentifier(identifier)) && attempts < 100000);

        if (identifier) reserved.add(canonicalizeExhibitStampIdentifier(identifier));
        next[entry.sourceKey] = {
            ...(next[entry.sourceKey] || {}),
            identifier,
            stamp_style: normalizeExhibitStampStyle(options?.stampStyle || 'auto')
        };
    });

    return next;
};

export const hasValidBatesSelectionSnapshot = (snapshot) => {
    if (snapshot === null
        || typeof snapshot !== 'object'
        || Array.isArray(snapshot)
        || !Number.isInteger(snapshot.case_id)
        || !Array.isArray(snapshot.files)
        || snapshot.files.length === 0
        || !/^[a-f0-9]{64}$/.test(String(snapshot.fingerprint || ''))) {
        return false;
    }

    if (snapshot.schema === 'casedb-bates-selection-v1') return true;
    if (snapshot.schema !== 'casedb-bates-selection-v2'
        || !Array.isArray(snapshot.ordered_folder_keys)
        || snapshot.ordered_folder_keys.length === 0) {
        return false;
    }

    const orderedFolderKeys = snapshot.ordered_folder_keys;
    return orderedFolderKeys.every(folderKey => (
        typeof folderKey === 'string'
        && folderKey.length > 0
        && folderKey.endsWith('/')
        && !/[\0\r\n]/.test(folderKey)
    )) && new Set(orderedFolderKeys).size === orderedFolderKeys.length;
};

export const isValidBatesTaskId = (taskId) => /^bates_[a-f0-9]{32}$/.test(String(taskId || ''));

export const isBatesSelectionChanged = (payload) => (
    payload?.reason_code === 'selection_changed'
    || payload?.response?.reason_code === 'selection_changed'
);

export const normalizeExhibitPageLabelSettings = (settings = {}) => ({
    enabled: settings.enabled === true,
    padding: Number(settings.padding),
    separator: String(settings.separator || ''),
    position: String(settings.position || '')
});

export const formatExhibitPageLabel = (identifier, pageNumber, settings = {}) => {
    const normalizedIdentifier = normalizeManualExhibitIdentifier(identifier);
    const match = normalizedIdentifier.match(/^(PTX|DTX|JTX)?([A-Z]?)(\d{1,8})([A-Z]?)$/);
    const padding = Number.isInteger(Number(settings.padding))
        ? Math.min(12, Math.max(1, Number(settings.padding)))
        : 3;
    const separator = EXHIBIT_PAGE_LABEL_SEPARATORS.has(settings.separator) ? settings.separator : '-';
    const normalizedPage = Number(pageNumber);
    if (!match || !Number.isInteger(normalizedPage) || normalizedPage < 1) return '';

    const typePrefix = match[1] || (match[2] ? '' : 'EX');
    const exhibitNumber = String(Number(match[3])).padStart(padding, '0');
    const page = String(normalizedPage).padStart(padding, '0');
    return `${typePrefix}${match[2] || ''}${exhibitNumber}${match[4] || ''}${separator}${page}`;
};

export const buildExhibitPageLabelPlan = (
    resolution,
    rawSettings = {},
    exhibitStampPosition = 'auto'
) => {
    const settings = normalizeExhibitPageLabelSettings(rawSettings);
    const errors = [];

    if (settings.enabled) {
        if (!Number.isInteger(settings.padding) || settings.padding < 1 || settings.padding > 12) {
            errors.push('Digit padding must be between 1 and 12.');
        }
        if (!EXHIBIT_PAGE_LABEL_SEPARATORS.has(settings.separator)) {
            errors.push('Choose a dash or period separator.');
        }
        if (!EXHIBIT_PAGE_LABEL_POSITIONS.has(settings.position)) {
            errors.push('Choose a page-label position.');
        }
        if (exhibitStampPosition !== 'auto' && exhibitStampPosition === settings.position) {
            errors.push('The exhibit stamp and page label cannot use the same fixed position.');
        }
        if (!resolution?.canStart) {
            errors.push('Resolve every exhibit identifier before adding page labels.');
        }
    }

    const firstEntry = (resolution?.processable || []).find(entry => entry.resolved) || null;
    const firstLabel = settings.enabled && firstEntry
        ? formatExhibitPageLabel(firstEntry.normalizedIdentifier, 1, settings)
        : '';
    const secondLabel = settings.enabled && firstEntry
        ? formatExhibitPageLabel(firstEntry.normalizedIdentifier, 2, settings)
        : '';

    return {
        enabled: settings.enabled,
        valid: !settings.enabled || errors.length === 0,
        errors,
        documentCount: (resolution?.processable || []).length,
        firstEntry,
        firstLabel,
        secondLabel,
        payload: {
            enabled: settings.enabled,
            padding: settings.padding,
            separator: settings.separator,
            position: settings.position
        }
    };
};

export const summarizeExhibitPageLabelRanges = (ranges) => {
    const savedRanges = Array.isArray(ranges)
        ? ranges.filter(range => {
            const outputStatus = String(range?.output_status || '').trim().toLowerCase();
            return !outputStatus || outputStatus === 'saved';
        })
        : [];
    if (savedRanges.length === 0) return '';

    const pageCount = savedRanges.reduce((total, range) => total + Math.max(0, Number(range?.pages) || 0), 0);
    const firstRange = savedRanges.find(range => String(range?.range || '').trim()) || savedRanges[0];
    const firstRangeText = String(firstRange?.range || '').trim()
        || [firstRange?.first_label, firstRange?.last_label].filter(Boolean).join(' to ');
    const pageText = `${pageCount} page${pageCount === 1 ? '' : 's'} labeled`;
    return firstRangeText ? `${pageText}; first document: ${firstRangeText}.` : `${pageText}.`;
};

const normalizeCompletionCount = (value) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) && numericValue > 0 ? Math.floor(numericValue) : 0;
};

export const normalizeBatesCompletionResult = (payload = {}) => {
    const response = payload?.response && typeof payload.response === 'object' && !Array.isArray(payload.response)
        ? payload.response
        : null;
    const result = response ? { ...payload, ...response } : { ...payload };

    const placementAdvisories = Array.isArray(response?.placement_advisories)
        ? response.placement_advisories
        : (Array.isArray(response?.exhibit_placement_fallbacks)
            ? response.exhibit_placement_fallbacks
            : (Array.isArray(payload?.placement_advisories)
                ? payload.placement_advisories
                : (Array.isArray(payload?.exhibit_placement_fallbacks)
                    ? payload.exhibit_placement_fallbacks
                    : [])));

    return {
        ...result,
        success: response ? response.success !== false : payload?.success !== false,
        processed_files: normalizeCompletionCount(
            response?.processed_files ?? payload?.processed_files ?? payload?.processed
        ),
        total_files: normalizeCompletionCount(
            response?.total_files ?? payload?.total_files ?? payload?.total
        ),
        selected_source_files: normalizeCompletionCount(
            response?.selected_source_files
                ?? payload?.selected_source_files
                ?? response?.total_files
                ?? payload?.total_files
                ?? payload?.total
        ),
        attempted_source_files: normalizeCompletionCount(
            response?.attempted_source_files
                ?? payload?.attempted_source_files
                ?? response?.total_files
                ?? payload?.total_files
                ?? payload?.total
        ),
        successful_source_files: normalizeCompletionCount(
            response?.successful_source_files
                ?? payload?.successful_source_files
                ?? response?.processed_files
                ?? payload?.processed_files
                ?? payload?.processed
        ),
        output_count: normalizeCompletionCount(
            response?.output_count
                ?? payload?.output_count
                ?? response?.output_files?.length
                ?? payload?.output_files?.length
        ),
        skipped_output_count: normalizeCompletionCount(
            response?.skipped_output_count
                ?? payload?.skipped_output_count
                ?? response?.skipped_files
                ?? payload?.skipped_files
        ),
        skipped_files: normalizeCompletionCount(
            response?.skipped_files ?? payload?.skipped_files
        ),
        skipped_outputs: Array.isArray(response?.skipped_outputs)
            ? response.skipped_outputs
            : (Array.isArray(payload?.skipped_outputs) ? payload.skipped_outputs : []),
        output_files: Array.isArray(response?.output_files)
            ? response.output_files
            : (Array.isArray(payload?.output_files) ? payload.output_files : []),
        page_label_ranges: Array.isArray(response?.page_label_ranges)
            ? response.page_label_ranges
            : (Array.isArray(payload?.page_label_ranges) ? payload.page_label_ranges : []),
        warnings: Array.isArray(response?.warnings)
            ? response.warnings
            : (Array.isArray(payload?.warnings) ? payload.warnings : []),
        placement_advisories: placementAdvisories,
        exhibit_placement_fallback_count: normalizeCompletionCount(
            response?.exhibit_placement_fallback_count
                ?? payload?.exhibit_placement_fallback_count
                ?? placementAdvisories.length
        )
    };
};

const summarizeSkippedOutputNames = (skippedOutputs, skippedCount) => {
    const names = (Array.isArray(skippedOutputs) ? skippedOutputs : [])
        .map(output => String(output?.final_name || output?.requested_name || '').trim())
        .filter(Boolean)
        .slice(0, 3);
    if (names.length === 0) return '';

    const remainingCount = Math.max(0, skippedCount - names.length);
    return ` Skipped: ${names.join(', ')}${remainingCount ? `, and ${remainingCount} more` : ''}.`;
};

export const buildBatesCompletionNotice = ({
    operationLabel = 'Bates',
    result = {},
    status = 'complete'
} = {}) => {
    const normalizedResult = normalizeBatesCompletionResult(result);
    const processedCount = normalizedResult.successful_source_files;
    const outputCount = normalizedResult.output_count;
    const skippedCount = normalizedResult.skipped_output_count;
    const isPartial = status === 'partial';
    const hasSkippedOutputs = skippedCount > 0;
    const completionState = isPartial
        ? 'partially completed'
        : (hasSkippedOutputs ? 'completed with skipped outputs' : 'completed');
    const processedNoun = processedCount === 1 ? 'source file' : 'source files';
    const outputNoun = outputCount === 1 ? 'output' : 'outputs';
    const skippedNoun = skippedCount === 1 ? 'output' : 'outputs';
    const pageLabelSummary = summarizeExhibitPageLabelRanges(normalizedResult.page_label_ranges);
    const placementAdvisoryCount = normalizedResult.exhibit_placement_fallback_count;

    let text = `${operationLabel} stamping ${completionState}! ${processedCount} ${processedNoun} processed.`;
    if (outputCount > 0) {
        text += ` ${outputCount} ${outputNoun} saved.`;
    }
    if (hasSkippedOutputs) {
        text += ` ${skippedCount} ${skippedNoun} skipped because matching files already existed; those existing files were left unchanged.`;
        text += summarizeSkippedOutputNames(normalizedResult.skipped_outputs, skippedCount);
    }
    if (pageLabelSummary) text += ` ${pageLabelSummary}`;
    if (placementAdvisoryCount > 0) {
        const identifiers = normalizedResult.placement_advisories
            .map(advisory => String(advisory?.exhibit_identifier || '').trim())
            .filter(Boolean);
        const displayedIdentifiers = identifiers.slice(0, 12);
        const remainingIdentifiers = Math.max(0, placementAdvisoryCount - displayedIdentifiers.length);
        text += ` ${placementAdvisoryCount} exhibit${placementAdvisoryCount === 1 ? '' : 's'} used the enabled non-strict placement fallback`;
        if (displayedIdentifiers.length > 0) {
            text += `: ${displayedIdentifiers.join(', ')}${remainingIdentifiers > 0 ? `, and ${remainingIdentifiers} more` : ''}`;
        }
        text += '. Exact placement rectangles and obstruction metrics are recorded in the exhibit manifest.';
    }
    if (isPartial && normalizedResult.warnings.length > 0) {
        text += ` Warnings: ${normalizedResult.warnings.join('; ')}.`;
    }

    return {
        text,
        type: isPartial || hasSkippedOutputs ? 'warning' : 'success',
        processedCount,
        outputCount,
        skippedCount,
        pageLabelSummary,
        placementAdvisoryCount
    };
};
